import { useEffect, useRef, useState } from "react"

import { useStripe, useElements, PaymentElement, ExpressCheckoutElement } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"

import styles from "./checkout_form.module.scss"

import { fetchAuthenticityToken } from "app/scripts/refresh_form_authenticity_token"
import PromoCodeForm from "app/components/subscriptions/promo_code_form"
import Tooltip from "shared/components/tooltip"

import { normalizePaymentMethodType } from "app/utils/payment_method_type"
import { logCustomDatadogAction } from "app/scripts/shared_setup"
import { captureException } from "@sentry/react"

// Stripe.js `confirmSetup` / `confirmPayment` reject with plain `StripeError` objects that don't extend Error.
// `new Error(String(error))` then yields "[object Object]" in Sentry, losing every actionable field.
// Wrap the error with a meaningful message and attach the Stripe-specific fields as `extra`.
const serializeStripeError = (error) => {
  if (error instanceof Error) {
    return { error, extras: undefined }
  }

  const isObject = error && typeof error === "object"
  const message = (isObject && (error.message || error.code)) || "Stripe error"
  const wrapped = new Error(message)
  if (isObject && error.code) {
    wrapped.name = `StripeError(${error.code})`
  }

  return {
    error: wrapped,
    extras: isObject
      ? {
        stripeError: {
          type: error.type,
          code: error.code,
          decline_code: error.decline_code,
          payment_method_type: error.payment_method?.type,
          request_log_url: error.request_log_url,
          message: error.message,
        },
      }
      : undefined,
  }
}

const CheckoutForm = ({
  buttonConfig,
  email,
  isBillingUpdateFlow,
  onCreateSubscriptionStart,
  onCreateSubscriptionCancel,
  onPaymentMethodSelect,
  onSetupIntentError,
  processedAfterPaymentMethodSetup,
  promoCodeConfig,
  subscriptionSuccessPath,
}) => {
  const stripe = useStripe()
  const elements = useElements()

  const [clientSecret, setClientSecret] = useState(null)
  const [returnUrl, setReturnUrl] = useState(null)
  const [intentError, setIntentError] = useState(null)
  const [intentSessionExpired, setIntentSessionExpired] = useState(false)
  const [isIntentLoading, setIsIntentLoading] = useState(true)
  const [handlingSubmit, setHandlingSubmit] = useState(false)
  const [walletsAvailable, setWalletsAvailable] = useState(null)
  const stripeLoadStartRef = useRef(null)
  const lastTrackedSelectionRef = useRef(null)

  useEffect(() => {
    fetchCreateIntent()
  }, [])

  // Stripe may call onReady without availablePaymentMethods; treat unresolved state as no wallets
  // after a short window so the placeholder does not persist forever.
  useEffect(() => {
    if (!stripe || !elements || walletsAvailable !== null) return undefined

    const timeoutId = window.setTimeout(() => {
      setWalletsAvailable(current => (current === null ? false : current))
    }, 2000)

    return () => window.clearTimeout(timeoutId)
  }, [stripe, elements, walletsAvailable])

  const toSetupIntentErrorMessage = error => {
    const statusHint = String(error?.message || "")
    if (statusHint.includes("429")) return "Too many payment requests right now. Please wait a moment and try again."
    return "We couldn't load the secure payment form. Please retry."
  }

  const postCreateIntent = async (path) => {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), 8000)
    try {
      const headers = {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": await fetchAuthenticityToken(),
      }

      return await fetch(path, {
        method: "POST",
        signal: controller.signal,
        headers,
      })
    } finally {
      window.clearTimeout(timeoutId)
    }
  }

  const fetchCreateIntent = async (maxAttempts = 3) => {
    setIsIntentLoading(true)
    setIntentError(null)
    setIntentSessionExpired(false)
    setClientSecret(null)

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const createIntentPath = "/subscriptions/create_intent"
        let res = await postCreateIntent(createIntentPath)

        if (res.status === 404) {
          res = await postCreateIntent("/subscriptions/create_intent.json")
        }

        // Session expired (cookie/CSRF no longer valid) — retrying 3× will not recover and
        // the generic "couldn't load" message hides the real cause from users. Short-circuit
        // with an explicit "refresh and sign in" CTA and skip the Sentry capture (expected).
        if (res.status === 401) {
          const message = "Your session has expired. Please refresh the page and sign in again."
          setIntentError(message)
          setIntentSessionExpired(true)
          setIsIntentLoading(false)
          onSetupIntentError?.(message)
          return
        }

        if (!res.ok) {
          logCustomDatadogAction("checkout_intent_fetch_failed", {
            status: res.status,
            path: res.url || createIntentPath,
            attempt,
          })
          const errorResponse = await res.json().catch(() => ({}))
          const apiErrorMessage = errorResponse?.error?.message
          throw new Error(apiErrorMessage || `HTTP ${res.status}`)
        }

        const result = await res.json()
        setClientSecret(result.clientSecret)
        setReturnUrl(result.returnUrl)
        setIsIntentLoading(false)
        return
      } catch (error) {
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 250))
        } else {
          const { error: serializedError, extras } = serializeStripeError(error)
          captureException(serializedError, {
            tags: { product_area: "subscriptions", checkout: "setup_intent" },
            ...(extras ? { extra: extras } : {}),
          })
          const message = toSetupIntentErrorMessage(error)
          setIntentError(message)
          setIsIntentLoading(false)
          onSetupIntentError?.(message)
        }
      }
    }
  }

  const openZendesk = (e) => {
    e.preventDefault()
    window.App.loadZendeskMessenger()
  }

  const SETUP_INTENT_SECRET_PATTERN = /^seti_.*secret_/
  const PAYMENT_INTENT_SECRET_PATTERN = /^pi_.*secret_/

  const confirmSetupWithRetry = async (options, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      const result = await stripe.confirmSetup(options)
      if (result.error?.code !== "lock_timeout" || i === maxRetries - 1) return result
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)))
    }
  }

  const confirmPaymentWithRetry = async (options, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      const result = await stripe.confirmPayment(options)
      if (result.error?.code !== "lock_timeout" || i === maxRetries - 1) return result
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)))
    }
  }

  const handleSubmit = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded. Make sure to disable
      // form submission until Stripe.js has loaded.
      setHandlingSubmit(false)
      return
    }

    if (!clientSecret || !returnUrl) {
      const message = "Payment form is still loading. Please retry in a moment."
      setIntentError(message)
      window.appendFlash(message, "danger")
      setHandlingSubmit(false)
      return
    }

    try {
      const submitResult = await elements.submit()
      if (submitResult.error) {
        window.appendFlash(submitResult.error.message, "danger")
        setHandlingSubmit(false)
        return
      }

      if (!submitResult.selectedPaymentMethod) {
        onCreateSubscriptionCancel({ message: "Please choose a payment method and try again." })
        setHandlingSubmit(false)
        return
      }

      await completeSetupIntent(submitResult.selectedPaymentMethod)
    } catch (error) {
      const { error: serializedError, extras } = serializeStripeError(error)
      captureException(serializedError, {
        tags: { product_area: "subscriptions", checkout: "checkout_submit" },
        ...(extras ? { extra: extras } : {}),
      })
      onCreateSubscriptionCancel(error)
      setHandlingSubmit(false)
    }
  }

  const completeSetupIntent = async (selectedPaymentMethod, options = {}) => {
    const onPaymentFailure = options.onPaymentFailure
    const paymentMethodType = normalizePaymentMethodType(selectedPaymentMethod)

    onCreateSubscriptionStart?.(paymentMethodType)

    const confirmParams = {
      return_url: returnUrl,
      payment_method_data: {
        billing_details: {
          address: {
            city: null,
            country: null,
            line1: null,
            line2: null,
            postal_code: null,
            state: null
          },
          email: email,
        },
      },
    }

    const confirmationResult = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams,
      redirect: "if_required"
    })

    if (confirmationResult.error) {
      onCreateSubscriptionCancel(confirmationResult.error)
      onPaymentFailure?.(confirmationResult.error.message || "Payment failed. Please try again.")
      setHandlingSubmit(false)
      return
    }

    const setupIntentPaymentMethod = confirmationResult.setupIntent?.payment_method
    const paymentMethodId = typeof setupIntentPaymentMethod === "string"
      ? setupIntentPaymentMethod
      : setupIntentPaymentMethod?.id

    if (!paymentMethodId) {
      const verificationError = {
        message: "We couldn't verify your payment method. Please try again."
      }
      onCreateSubscriptionCancel(verificationError)
      onPaymentFailure?.(verificationError.message)
      setHandlingSubmit(false)
      return
    }

    const requiresAction = async (intentClientSecret, subscriptionId) => {
      // Stripe requires elements.submit() before each confirmPayment call, even after the
      // initial confirmSetup — the clientSecret changed so the Elements state must be re-validated.
      const {error: resubmitError} = await elements.submit()
      if (resubmitError) throw resubmitError

      const successConfirmParams = subscriptionSuccessPath
        ? { ...confirmParams, return_url: `${subscriptionSuccessPath.replace(/\/$/, "")}/${subscriptionId}` }
        : confirmParams

      if (intentClientSecret.match(SETUP_INTENT_SECRET_PATTERN)) {
        const { error, setupIntent } = await confirmSetupWithRetry({
          elements,
          clientSecret: intentClientSecret,
          confirmParams: successConfirmParams,
          redirect: "if_required",
        })

        if (error) {
          // User is already trialing and will receive notification again from Stripe to confirm the payment before trial finishes.
          // We'll throw error just to track but we'll not stop user.
          throw error
        } else if (setupIntent.status === "succeeded") {
          fetch("/subscriptions/update_customer_default_payment_method.json", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": await fetchAuthenticityToken(),
            },
            body: JSON.stringify({
              payment_method: setupIntent.payment_method,
            }),
          })
        }
      } else if (intentClientSecret.match(PAYMENT_INTENT_SECRET_PATTERN)) {
        const {error} = await confirmPaymentWithRetry({
          elements,
          clientSecret: intentClientSecret,
          confirmParams: successConfirmParams,
          redirect: "if_required"
        })

        if (error) throw error
      } else {
        throw new Error("Invalid client secret format")
      }
    }

    processedAfterPaymentMethodSetup(paymentMethodId, requiresAction, () => {
      setHandlingSubmit(false)
      fetchCreateIntent()
    }, paymentMethodType)
  }

  const trackSelection = rawPaymentMethodType => {
    const paymentMethodType = normalizePaymentMethodType(rawPaymentMethodType)
    if (lastTrackedSelectionRef.current === paymentMethodType) return
    lastTrackedSelectionRef.current = paymentMethodType
    onPaymentMethodSelect?.(paymentMethodType)
  }

  const onPaymentElementChange = event => {
    trackSelection(event?.value?.type)
  }

  const onExpressCheckoutConfirm = async event => {
    if (handlingSubmit) return
    setHandlingSubmit(true)

    if (!stripe || !elements) {
      setHandlingSubmit(false)
      return
    }

    if (!clientSecret || !returnUrl) {
      const message = "Payment form is still loading. Please retry in a moment."
      setIntentError(message)
      window.appendFlash(message, "danger")
      event?.paymentFailed?.({ message })
      setHandlingSubmit(false)
      return
    }

    try {
      const submitResult = await elements.submit()
      if (submitResult.error) {
        const message = submitResult.error.message || "Payment failed. Please try again."
        logCustomDatadogAction("express_checkout_submit_validation_failed", {
          error_code: submitResult.error.code,
          error_type: submitResult.error.type,
          express_payment_type: event?.expressPaymentType,
        })
        event?.paymentFailed?.({ message })
        window.appendFlash(message, "danger")
        setHandlingSubmit(false)
        return
      }

      const paymentMethodType = normalizePaymentMethodType(event?.expressPaymentType)
      trackSelection(paymentMethodType)
      await completeSetupIntent(paymentMethodType, {
        onPaymentFailure: message => {
          event?.paymentFailed?.({ message })
        },
      })
    } catch (error) {
      const { error: serializedError, extras } = serializeStripeError(error)
      captureException(serializedError, {
        tags: { product_area: "subscriptions", checkout: "express_checkout_submit" },
        ...(extras ? { extra: extras } : {}),
      })
      const message = error?.message || "Payment failed. Please try again."
      event?.paymentFailed?.({ message })
      onCreateSubscriptionCancel(error)
      setHandlingSubmit(false)
    }
  }

  const onExpressCheckoutReady = event => {
    const availablePaymentMethods = event?.availablePaymentMethods
    const linkOnly =
      Boolean(availablePaymentMethods?.link)
      && !availablePaymentMethods?.applePay
      && !availablePaymentMethods?.googlePay

    logCustomDatadogAction("express_checkout_ready", {
      has_available_payment_methods: availablePaymentMethods !== undefined,
      apple_pay_available: Boolean(availablePaymentMethods?.applePay),
      google_pay_available: Boolean(availablePaymentMethods?.googlePay),
      link_available: Boolean(availablePaymentMethods?.link),
      link_only: linkOnly,
    })

    // undefined means Stripe determined no express methods can show in this browser/context.
    if (availablePaymentMethods === undefined) {
      setWalletsAvailable(false)
      return
    }

    // Link-only users must count here; #10259 set link: "auto" but row visibility still depends on this.
    const hasWallets = Boolean(
      availablePaymentMethods.applePay
      || availablePaymentMethods.googlePay
      || availablePaymentMethods.link
    )
    setWalletsAvailable(hasWallets)
  }

  const onExpressCheckoutLoadError = () => {
    setWalletsAvailable(false)
    logCustomDatadogAction("express_checkout_load_error")
  }

  const autoFocus = element => {
    if (!window.App.integrationTestMode) element.focus()
  }

  const loaderStartTrack = () => {
    stripeLoadStartRef.current = performance.now()
  }

  const readyTrack = (element) => {
    const stripeLoadTime = performance.now() - stripeLoadStartRef.current

    autoFocus(element)

    logCustomDatadogAction("stripe_load_completed", { loadTime: stripeLoadTime })
  }

  const stripeReady = Boolean(stripe && elements)
  const paymentFormReady =
    stripeReady && Boolean(clientSecret) && !isIntentLoading && !intentError
  const showCheckoutTooltip = paymentFormReady && !handlingSubmit
  // Billing update matches production: Apple Pay / Google Pay as Payment Element tabs, not express buttons.
  const useExpressCheckout = !isBillingUpdateFlow
  const showExpressCheckout = useExpressCheckout && stripeReady && walletsAvailable === true

  const walletsResolved = !useExpressCheckout || walletsAvailable !== null

  return (
    <form
      className={`form payment-info-form ${styles["payment-info-form"]} ${isBillingUpdateFlow ? styles["payment-info-form--billing-update"] : ""}`}
      data-testid="checkout-form"
      data-wallets-resolved={walletsResolved ? "true" : "false"}
      onSubmit={handleSubmit}
    >
      {useExpressCheckout && stripeReady && (
        <div
          className={styles["express-checkout-slot"]}
          data-testid="express-checkout-slot"
          hidden={walletsAvailable === false}
        >
          <div className={styles["express-checkout-inner"]}>
            <ExpressCheckoutElement
              onReady={onExpressCheckoutReady}
              onLoadError={onExpressCheckoutLoadError}
              onConfirm={onExpressCheckoutConfirm}
              options={{
                buttonType: {
                  applePay: isBillingUpdateFlow ? "pay" : "subscribe",
                  googlePay: isBillingUpdateFlow ? "pay" : "subscribe",
                },
                buttonTheme: {
                  applePay: "black",
                },
                layout: {
                  overflow: "never",
                },
                paymentMethods: {
                  applePay: "auto",
                  googlePay: "auto",
                  link: "auto",
                  amazonPay: "never",
                  paypal: "never",
                  klarna: "never",
                },
                billingAddressRequired: false,
                emailRequired: false,
                shippingAddressRequired: false,
              }}
            />
            {walletsAvailable === null && <div className={styles["express-checkout-skeleton"]} aria-hidden />}
          </div>
        </div>
      )}

      {showExpressCheckout && (
        <div className="or-divider" aria-hidden>
          <span>or</span>
        </div>
      )}

      <div className={styles["payment-element-slot"]}>
        {stripeReady ? (
          <div className={`${styles["stripe-mount"]} credit-card-field`}>
            <PaymentElement
              onChange={onPaymentElementChange}
              onLoaderStart={loaderStartTrack}
              onReady={readyTrack}
              options={{
                fields: {
                  billingDetails: {
                    address: "never",
                    email: "never",
                  },
                },
                layout: {
                  type: "tabs",
                },
                defaultValues: {
                  billingDetails: {
                    email: email,
                  },
                },
                terms: {
                  card: "never"
                },
                wallets: isBillingUpdateFlow
                  ? { link: "never" }
                  : {
                      applePay: "never",
                      googlePay: "never",
                      link: "never",
                    }
              }}
            />

            {promoCodeConfig?.initialPromoCode && (<PromoCodeForm {...promoCodeConfig} />)}
          </div>
        ) : (
          <div
            className={styles["payment-element-placeholder"]}
            aria-busy="true"
            aria-live="polite"
            aria-label="Loading payment form"
          >
            <div className={styles["skeleton-wallet-row"]} aria-hidden>
              <div className={styles["skeleton-wallet-button"]} />
              <div className={styles["skeleton-wallet-button"]} />
            </div>
            <div className="or-divider" aria-hidden>
              <span>or</span>
            </div>
            <div className={styles["skeleton-field"]} aria-hidden />
            <div className={styles["skeleton-split-row"]} aria-hidden>
              <div className={styles["skeleton-field-half"]} />
              <div className={styles["skeleton-field-half"]} />
            </div>
            <p className={styles["status-copy"]}>
              Loading secure payment form...
            </p>
          </div>
        )}
      </div>

      {intentError && (
        <div className={styles["intent-error"]} aria-live="polite">
          <p>{intentError}</p>
          {intentSessionExpired ? (
            <button
              type="button"
              className="link-inline"
              onClick={() => window.location.reload()}
            >
              Refresh page
            </button>
          ) : (
            <button
              type="button"
              className="link-inline"
              onClick={() => fetchCreateIntent()}
            >
              Retry loading payment form
            </button>
          )}
        </div>
      )}

      <section className={styles["action-buttons"]}>
        <button
          id="submit-payment"
          type="submit"
          className={buttonConfig.buttonType ? "button-secondary full-width" : "button-primary full-width"}
          title={buttonConfig.cta}
          aria-label={buttonConfig.cta}
          data-dd-action-name="Subscription credit card payment button"
          disabled={!paymentFormReady || handlingSubmit}
        >
          {handlingSubmit ? buttonConfig.submittingCopy : buttonConfig.cta}
          {showCheckoutTooltip && (
            <Tooltip>
              Please be sure you’ve correctly completed the form. <br />
              <a className="link-inline" onClick={openZendesk}>
                Contact us
              </a>
              for help.
            </Tooltip>
          )}
        </button>
        <p className="cta-fineprint">
          {buttonConfig.showTrialFineprint && (
            <>
              No charge for 7 days. Cancel anytime.
              <br />
            </>
          )}
          Need help?{" "}
          <a href="#" className="fineprint-link" onClick={openZendesk}>
            Talk to us
          </a>
        </p>
      </section>
    </form>
  )
}

CheckoutForm.propTypes = {
  buttonConfig: PropTypes.shape({
    buttonType: PropTypes.string,
    cta: PropTypes.string.isRequired,
    submittingCopy: PropTypes.string.isRequired,
    showTrialFineprint: PropTypes.bool,
  }),
  email: PropTypes.string.isRequired,
  isBillingUpdateFlow: PropTypes.bool,
  onCreateSubscriptionCancel: PropTypes.func,
  onCreateSubscriptionStart: PropTypes.func,
  onPaymentMethodSelect: PropTypes.func,
  onSetupIntentError: PropTypes.func,
  processedAfterPaymentMethodSetup: PropTypes.func.isRequired,
  promoCodeConfig: PropTypes.shape(),
  subscriptionSuccessPath: PropTypes.string,
}

export default CheckoutForm
